// src/components/BoundedField.tsx
//
// ★★ WAVE 8 (Task 3) — ONE FIELD THAT SHOWS THE RULE THE SERVER PUBLISHED, used
// by all THIRTEEN administration write forms.
//
// ══ WHY ONE COMPONENT AND NOT THIRTY-NINE FIELDS ══
//
// Thirty-nine write-form fields across thirteen catalogues carry a bound the server
// enforces — counted against the tree on 2026-08-20 and asserted as a floor by
// `published-bounds.test.ts`, because "thirty-seven across twelve" stood in this
// header for one commit and the whole purpose of this file is keeping one number
// true. Wiring each one by hand would put thirty-nine copies of the same
// `aria-describedby` composition in the tree, and `FormResult`'s own header
// records what happened the last time this app had eleven copies of one idea: the
// attribute is the easy half, and eleven screens got the wrong half. So the
// composition is made once, here.
//
// ══ ⚠ WHY A NATIVE `<input>` RATHER THAN `<SgdsInput placeholder="">` ══
//
// This is the same reason `WardClassesPage`'s rank-order field already gives, and
// it is a wiring fact, not a preference: **`aria-describedby` has to name an
// element in the SAME document**, and an SGDS component keeps its input inside a
// shadow root where an id in the light DOM cannot reach it. A field-level error
// (or a published rule) that the input does not reference is a paragraph sitting
// near a box, not a description of it.
//
// `ReferenceDataPage` — the screen this task generalises — made the same choice
// for the same reason, and its markup is what this component's is copied from,
// down to the `pt-native-date` class. That class is styled entirely from SGDS
// tokens (`--sgds-border-color-default`, `--sgds-surface-default`,
// `--sgds-outline-focus`), so nothing here invents a token value.
//
// ══ ⚠ WHICH BOUNDS BECOME ATTRIBUTES, AND WHICH DO NOT ══
//
//   · **`maxLength` becomes an attribute.** It makes the server's ceiling
//     unreachable rather than merely announced, and it cannot refuse anything the
//     route would have accepted: trimming and upper-casing never LENGTHEN a value.
//   · **`pattern` does NOT.** These forms transform before submitting
//     (`code.trim().toUpperCase()`), so the browser would run constraint
//     validation on the raw keystrokes and refuse `b2` for failing `^[A-Z]…` when
//     the route would have accepted the `B2` about to be sent. Worse, it refuses
//     it SILENTLY — `e2e/write-forms.spec.ts`'s header records a debugging session
//     lost to exactly that, because `sgds-button` submits by clicking an injected
//     button so no `submit` event fires and nothing is announced. The pattern is
//     shown in words and checked by `boundsProblem` against the value that will
//     actually be sent, which is both announced and correct.
//   · **`min` does NOT either**, and `type` stays `text`: a `type="number"` input
//     DISCARDS `1e999` before this app can see it, which is measured at length in
//     `FieldError.tsx`'s header. `inputMode="numeric"` keeps the numeric keypad.
//
// ⚠ **`bounds === undefined` renders NO rule.** That happens for real: a non-PSD
// officer's list request is refused, so no payload arrives and there is nothing
// published to show. A fallback here would be an invented bound — the exact defect
// this task removes — so the field simply says nothing beyond `required`, and the
// route refuses the submission on its own as it always did.
import { useId } from 'react'
import { FieldError, fieldErrorProps } from './FieldError'
import { boundsSentence } from '../lib/published-bounds'
import type { PublishedFieldBounds } from '../lib/types'

export interface BoundedFieldProps {
  /** The visible label. Comes from the calling form's one label map, never duplicated. */
  readonly label: string
  readonly value: string
  readonly onValueChange: (next: string) => void
  /** The bounds the SERVER published for this field, or `undefined` when it published none. */
  readonly bounds: PublishedFieldBounds | undefined
  /**
   * The field-level problem, from `boundsProblems` in the calling form.
   *
   * ⚠ Passed IN rather than computed here, so the form's submit button can be armed
   * off the same value — `WardClassesPage`'s note says why a field the screen
   * already knows is malformed must not be sent.
   */
  readonly problem: string | null
  readonly required?: boolean
  /**
   * An example, in the screen's own words ("e.g. WELCOME").
   *
   * ⚠ Presentation, NOT a bound. It must never state a limit or a syntax — those
   * come from `bounds` — and the census refuses a numeric limit written here.
   */
  readonly example?: string
  readonly multiline?: boolean
  readonly rows?: number
  readonly inputMode?: 'numeric'
  /** Grid placement for the calling form's two-column layout. */
  readonly className?: string
}

export function BoundedField({
  label,
  value,
  onValueChange,
  bounds,
  problem,
  required = false,
  example,
  multiline = false,
  rows = 4,
  inputMode,
  className,
}: BoundedFieldProps) {
  const base = useId()
  const inputId = `${base}-input`
  const hintId = `${base}-hint`
  const errorId = `${base}-error`

  const rule = boundsSentence(bounds)
  const hint = [example, rule].filter((part): part is string => part !== undefined && part !== null).join(' ')
  // ⚠ The hint id is named ONLY when there is a hint to read. See `fieldErrorProps`.
  const aria = fieldErrorProps(errorId, problem, [hint.length > 0 ? hintId : null])
  const shared = {
    id: inputId,
    className: 'pt-native-date',
    value,
    ...(required && { required: true }),
    ...(bounds?.maxLength !== undefined && { maxLength: bounds.maxLength }),
    ...aria,
  }

  /*
    ⚠ **`htmlFor`, NOT a `<label>` wrapped around the input**, and the difference
    is audible. A wrapping label contributes ALL of its text to the field's
    accessible NAME — so the published rule and the error message would be read
    out as part of the field's name *and* again as its description, and the name
    of a "Code" field would become "Code required e.g. WELCOME matching
    ^[A-Z]…$". Explicit association keeps the name the label and the rule the
    description, which is the split `aria-describedby` exists for.
  */
  return (
    <div className={`flex flex-col gap-1 text-sm${className === undefined ? '' : ` ${className}`}`}>
      <label htmlFor={inputId} className="font-semibold">
        {label}
        {required && (
          <>
            {' '}
            <span aria-hidden="true">*</span>
            <span className="sr-only">required</span>
          </>
        )}
      </label>
      {multiline ? (
        <textarea {...shared} rows={rows} onChange={(e) => onValueChange(e.target.value)} />
      ) : (
        <input
          {...shared}
          type="text"
          {...(inputMode !== undefined && { inputMode })}
          onChange={(e) => onValueChange(e.target.value)}
        />
      )}
      {hint.length > 0 && (
        <span id={hintId} className="text-xs text-[var(--sgds-body-color-subtle)]">
          {hint}
        </span>
      )}
      <FieldError id={errorId} problem={problem} />
    </div>
  )
}
